import { AddCommentModal } from "@/components/Comments";
import {
  MilestoneApprover,
  MilestoneErrorModal,
  MilestoneUser,
  MilestoneUsersContainer,
  UserSelect,
} from "@/containers/Project/components";
import * as S from "@/containers/Project/components/styled";
import { hasRequiredApprovers } from "@/containers/Project/helper";
import { CommentTarget, GUTTER_SIZE, ProjectMilestoneStatus } from "@/enums";
import { useIsAdmin } from "@/helpers/permissions";
import { projectMilestoneType } from "@/propTypes";
import { useProjectContext, useUserContext } from "@/providers";
import { useGetFormLookupsQuery } from "@/services";
import { Button, Form, Row, Space, Typography } from "antd";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { getActionFromStatus } from "../Modals/MilestoneStatus/helper";
import { buildUpdatedApprovers, checkIsAllApproversApproved } from "./helper";

export const MilestoneApproversView = ({ milestone }) => {
  const intl = useIntl();
  const [form] = Form.useForm();
  const { userToken } = useUserContext();
  const {
    project,
    updateMilestone,
    milestoneStatusChange,
    handleNotifyApprovers,
  } = useProjectContext();
  const { projectLeaders, backupProjectLeader } = project || {};
  const isAdmin = useIsAdmin();

  const isProjectLeader =
    (!!projectLeaders &&
      Object.values(projectLeaders).includes(userToken.user.id)) ||
    backupProjectLeader === userToken.user.id;

  const labels = {
    edit: intl.formatMessage({ id: "general.edit" }),
    save: intl.formatMessage({ id: "general.save" }),
    cancel: intl.formatMessage({ id: "general.cancel" }),
  };

  const [regions, setRegions] = useState(null);
  const [shouldShowCancelButton, setShouldShowCancelButton] = useState(false);
  const [shouldShowEditView, setShouldShowEditView] = useState(false);
  const [isSavingApproverReplace, setIsSavingApproverReplace] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [pendingMilestoneUpdate, setPendingMilestoneUpdate] = useState(null);
  const [editUserButtonLabel, setEditUserButtonLabel] = useState(labels.edit);
  const [approversToNotify, setApproversToNotify] = useState([]);
  const [errorModal, setErrorModal] = useState({
    visible: false,
    title: null,
    description: null,
    errors: [],
  });

  const { data: forms } = useGetFormLookupsQuery();

  useEffect(() => {
    setRegions(forms?.intake?.region);
  }, [forms?.intake]);

  useEffect(() => {
    setIsSavingApproverReplace(false);
    setShouldShowCancelButton(false);
    setShouldShowEditView(false);
    setEditUserButtonLabel(labels.edit);

    if (approversToNotify.length > 0) {
      handleNotifyApprovers(project.id, milestone.id, approversToNotify);
    }

    if (
      !regions ||
      !(
        checkIsAllApproversApproved(milestone, projectLeaders) &&
        milestone.status !== ProjectMilestoneStatus.Approved &&
        milestone.status !== ProjectMilestoneStatus.Skipped
      )
    ) {
      return;
    }

    milestoneStatusChange(
      project.id,
      milestone.id,
      getActionFromStatus(ProjectMilestoneStatus.Approved),
      "",
      form
    );
  }, [milestone, regions]);

  if (!regions) {
    return <Form form={form} component={false} />;
  }

  const additionalApprovers = milestone.approvers
    ?.filter((x) => !x.regionId)
    ?.map((x) => x.assignedUsers)
    .flat();

  const shouldShowEditButton =
    milestone.status === ProjectMilestoneStatus.InApproval &&
    (isAdmin || isProjectLeader);

  const handleEditButtonClick = () => {
    const isCurrentlyEditing = editUserButtonLabel === labels.edit;

    if (isCurrentlyEditing) {
      setShouldShowCancelButton(true);
      setShouldShowEditView(true);
      setIsSavingApproverReplace(false);
      setEditUserButtonLabel(labels.save);
    } else {
      handleSave();
    }
  };

  const handleCancelButtonClick = () => {
    setShouldShowCancelButton(false);
    setShouldShowEditView(false);
    setEditUserButtonLabel(labels.edit);
  };

  const handleCloseModal = () => {
    setErrorModal({
      visible: false,
      title: null,
      description: null,
      errors: [],
    });
  };

  const handleSave = () => {
    const formValues = form.getFieldsValue(true);

    const { approvers, newApproversToNotify } = buildUpdatedApprovers(
      milestone.approvers,
      formValues
    );

    const leaderApprovers = milestone.projectLeaderApprovers?.map(
      (approver) => {
        if (!formValues[approver.id]) return approver;

        return {
          ...approver,
          isNotApplicable:
            formValues[approver.id].isNotApplicable ?? approver.isNotApplicable,
        };
      }
    );

    const updatedMilestone = {
      ...milestone,
      approvers,
      projectLeaderApprovers: leaderApprovers,
    };

    if (!hasRequiredApprovers(updatedMilestone, project?.projectLeaders)) {
      setErrorModal({
        visible: true,
        title: intl.formatMessage(
          { id: `milestonesList.milestoneSaveApproverReplaceTitle` },
          { milestoneName: milestone.name }
        ),
        description: intl.formatMessage({
          id: `milestonesList.milestoneSaveApproverReplaceDescription`,
        }),
        errors: [],
      });
      return;
    }

    setApproversToNotify(newApproversToNotify);
    setPendingMilestoneUpdate(updatedMilestone);
    setShowCommentModal(true);
  };

  const handleFinishSave = (callback) => {
    if (!pendingMilestoneUpdate) return;

    setIsSavingApproverReplace(true);
    updateMilestone(pendingMilestoneUpdate);
    callback();
  };

  return (
    <>
      <Form form={form} layout="vertical">
        {userToken?.user?.id && (
          <S.MilestoneDetailCard
            styles={{ body: { padding: 0 } }}
            extra={
              shouldShowEditButton && (
                <Space direction="horizontal" size="small">
                  {shouldShowCancelButton && (
                    <Button
                      type="primary"
                      data-test-id="milestone-approvers-view-cancel-btn"
                      onClick={handleCancelButtonClick}
                    >
                      {labels.cancel}
                    </Button>
                  )}

                  <Button
                    type="primary"
                    data-test-id="milestone-approvers-view-replace-btn"
                    onClick={handleEditButtonClick}
                    loading={isSavingApproverReplace}
                  >
                    {editUserButtonLabel}
                  </Button>
                </Space>
              )
            }
          >
            <Row gutter={GUTTER_SIZE.large}>
              {Object.keys(regions).map((region) => (
                <MilestoneApprover
                  key={region}
                  region={region}
                  milestone={milestone}
                  shouldShowEditView={shouldShowEditView}
                  form={form}
                />
              ))}
              <S.RegionDisplayCol flex="1">
                {!!additionalApprovers?.length && (
                  <>
                    <Typography.Title level={4}>
                      {intl.formatMessage({
                        id: "project.approvers.additionalApproversHeader",
                      })}
                    </Typography.Title>
                    {shouldShowEditView && (
                      <Form.Item
                        label={intl.formatMessage({
                          id: "project.approvers.addApproverField",
                        })}
                        name="additionalApprovers"
                        initialValue={(additionalApprovers || []).map(
                          (user) => user.userId
                        )}
                      >
                        <UserSelect
                          users={(additionalApprovers || []).map(
                            (user) => user.userId
                          )}
                          readOnly={false}
                        />
                      </Form.Item>
                    )}
                    {!shouldShowEditView && (
                      <MilestoneUsersContainer
                        subTitle={intl.formatMessage({
                          id: "project.approvers.addApproverField",
                        })}
                      >
                        {additionalApprovers.map((approver) => (
                          <MilestoneUser
                            key={approver.userId}
                            user={approver}
                            milestone={milestone}
                            dataTestId={`milestone-${milestone.id}-approvers-view-user-${approver.userId}`}
                          />
                        ))}
                      </MilestoneUsersContainer>
                    )}
                  </>
                )}
              </S.RegionDisplayCol>
            </Row>
          </S.MilestoneDetailCard>
        )}
        <AddCommentModal
          title={intl.formatMessage({ id: "comments.approverReplaceTitle" })}
          isVisible={showCommentModal}
          target={CommentTarget.Milestone}
          targetId={milestone.id}
          projectId={project.id}
          tagName="Approver Replace"
          setVisible={setShowCommentModal}
          onFinish={(callback) => handleFinishSave(callback)}
        />
        <MilestoneErrorModal
          modalState={errorModal}
          closeModal={handleCloseModal}
        />
      </Form>
    </>
  );
};

MilestoneApproversView.propTypes = {
  milestone: projectMilestoneType.isRequired,
};
